import type {
  ClientCommunicationChannelName,
  ClientCommunicationChannels,
} from './clients-admin.models';

export type ClientReachabilityTone = 'automatic' | 'manual' | 'unreachable';

export interface ClientCommunicationChannelView {
  id: ClientCommunicationChannelName;
  title: string;
  value: string;
  meta: string;
  tone: ClientReachabilityTone;
}

export interface ClientCommunicationSummaryView {
  label: string;
  meta: string;
  tone: ClientReachabilityTone;
  channels: ClientCommunicationChannelView[];
}

export function presentClientCommunicationChannels(
  channels: ClientCommunicationChannels,
): ClientCommunicationSummaryView {
  const summary = channels.summary;
  const tone = summary.reachabilityStatus;

  return {
    label: reachabilityLabel(tone),
    meta: reachabilityMeta(summary.automaticChannels, summary.manualChannels),
    tone,
    channels: [
      {
        id: 'email',
        title: 'Email',
        value: channels.email.status === 'available' ? 'Available' : 'Missing',
        meta: channels.email.status === 'available' ? 'Automatic delivery' : 'No email channel',
        tone: channels.email.status === 'available' ? 'automatic' : 'unreachable',
      },
      {
        id: 'telegram',
        title: 'Telegram',
        value: telegramStatusLabel(channels.telegram.status, channels.telegram.username),
        meta: telegramStatusMeta(channels.telegram.status, channels.telegram.lastDeliveryError),
        tone: channels.telegram.status === 'active' ? 'automatic' : 'unreachable',
      },
      {
        id: 'phone',
        title: 'Phone',
        value: channels.phone.status === 'manual' ? 'Available' : 'Missing',
        meta: channels.phone.status === 'manual' ? 'Manual follow-up only' : 'No phone channel',
        tone: channels.phone.status === 'manual' ? 'manual' : 'unreachable',
      },
    ],
  };
}

function reachabilityLabel(status: ClientReachabilityTone): string {
  if (status === 'automatic') {
    return 'Automatically reachable';
  }

  if (status === 'manual') {
    return 'Manual follow-up required';
  }

  return 'Unreachable';
}

function reachabilityMeta(
  automaticChannels: ClientCommunicationChannelName[],
  manualChannels: ClientCommunicationChannelName[],
): string {
  const parts: string[] = [];

  if (automaticChannels.length > 0) {
    parts.push(`Automatic: ${automaticChannels.join(', ')}`);
  }

  if (manualChannels.length > 0) {
    parts.push(`Manual: ${manualChannels.join(', ')}`);
  }

  return parts.join(' · ') || 'No communication channel';
}

function telegramStatusLabel(
  status: ClientCommunicationChannels['telegram']['status'],
  username: string | null,
): string {
  if (status === 'active') {
    return username ? `Connected: @${username}` : 'Connected';
  }

  if (status === 'blocked') {
    return 'Blocked';
  }

  if (status === 'failed') {
    return 'Delivery failed';
  }

  if (status === 'revoked') {
    return 'Revoked';
  }

  return 'Not connected';
}

function telegramStatusMeta(
  status: ClientCommunicationChannels['telegram']['status'],
  lastDeliveryError: string | null,
): string {
  if (status === 'active') {
    return 'Automatic delivery';
  }

  if (status === 'blocked') {
    return lastDeliveryError ? `Blocked: ${lastDeliveryError}` : 'Client blocked the bot';
  }

  if (status === 'failed') {
    return lastDeliveryError ? `Last error: ${lastDeliveryError}` : 'Last delivery failed';
  }

  if (status === 'revoked') {
    return 'Link was revoked';
  }

  return 'No Telegram link';
}
